import { useRoute } from '#imports'
import { type ComputedRef, computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import { useBuybackConfig } from '@backmarket/nuxt-layer-buyback/app/composables/config/useBuybackConfig'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'

import { ROUTE_NAMES } from '~/scopes/buyback/constants'

import { useBuybackFunnelOffer } from './useBuybackFunnelOffer'

export function useBuybackFunnelSteps() {
  const route = useRoute()
  const experiments = useExperiments()
  const buybackConfig = useBuybackConfig()
  const { offer } = useBuybackFunnelOffer()

  const hasBankStep: ComputedRef<boolean> = computed(
    () =>
      Boolean(offer.value?.bankDetailsRequired) &&
      buybackConfig.hasBankStep &&
      experiments['experiment.resaleBankStep'] !== 'withoutBankStep',
  )

  const isSmartphoneCategory: ComputedRef<boolean> = computed(() => {
    const categoryId = route.params.id

    return !Array.isArray(categoryId) && categoryId === 'smartphone'
  })

  const hasImeiStep: ComputedRef<boolean> = computed(
    () =>
      isSmartphoneCategory.value &&
      (experiments['experiment.resaleImeiStepV2'] === 'withMandatoryImei' ||
        experiments['experiment.resaleImeiStepV2'] === 'withOptionalImei'),
  )

  const isImeiMandatory: ComputedRef<boolean> = computed(
    () => experiments['experiment.resaleImeiStepV2'] === 'withMandatoryImei',
  )

  const nextStepAfterImei: ComputedRef<RouteLocationRaw> = computed(() => {
    return hasBankStep.value
      ? {
          name: ROUTE_NAMES.BANK,
          params: { ...route.params },
          query: { ...route.query },
        }
      : {
          name: ROUTE_NAMES.ADDRESS,
          params: { ...route.params },
          query: { ...route.query },
        }
  })

  const nextStepAfterOffer: ComputedRef<RouteLocationRaw> = computed(() => {
    if (hasImeiStep.value) {
      return {
        name: ROUTE_NAMES.IMEI,
        params: { ...route.params },
        query: { ...route.query },
      }
    }

    return nextStepAfterImei.value
  })

  return {
    hasBankStep,
    hasImeiStep,
    isImeiMandatory,
    nextStepAfterOffer,
    nextStepAfterImei,
  }
}
